<i18n>
{
	"en": {
		"autoSchedule": "Generate automatically",
		"autoScheduleInfo": "Trips will be generated 14 days ahead every hour. Trips can be generated on demand from the trip templates overview",
		"yearlyInfo": "Trips will be scheduled on the specified dates every year.",
		"frequency": "Frequency",
		"selectFrequency": "Select frequency",
		"startDate": "Start date",
		"endDate": "End date",
		"selectDate": "Select date",
		"selectDays": "Select days",
		"runEvery": "Every",
		"monday": "Monday",
		"tuesday": "Tuesday",
		"wednesday": "Wednesday",
		"thursday": "Thursday",
		"friday": "Friday",
		"saturday": "Saturday",
		"sunday": "Sunday",
		"daily": "Daily",
		"weekly": "Weekly",
		"twoWeekly": "14 days",
		"monthly": "Monthly",
		"specificDates": "Reoccurring dates",
		"oddWeeks": "Odd weeks",
		"evenWeeks": "Even weeks",
		"nextTrip": "Next generation",
		"tripsExist": "Trips may have already been generated from this template. Please remove their connection manually if they should be generated again"
	},

	"no": {
		"autoSchedule": "Generer automatisk",
		"autoScheduleInfo": "Turer vil bli generert 14 dager frem i tid hver time. Turer kan bli generert manuellt fra turmal-siden",
		"yearlyInfo": "Turer opprettes på de gitte datoene hvert år.",
		"frequency": "Frekvens",
		"selectFrequency": "Velg frekvens",
		"startDate": "Stardato",
		"endDate": "Sluttdato",
		"selectDate": "Velg dato",
		"selectDays": "Velg dager",
		"runEvery": "Hver",
		"monday": "Mandag",
		"tuesday": "Tirsdag",
		"wednesday": "Onsdag",
		"thursday": "Torsdag",
		"friday": "Fredag",
		"saturday": "Lørdag",
		"sunday": "Søndag",
		"daily": "Daglig",
		"weekly": "Ukentlig",
		"twoWeekly": "14 dager",
		"monthly": "Månedlig",
		"specificDates": "Faste datoer",
		"oddWeeks": "Oddetallsuker",
		"evenWeeks": "Partallsuker",
		"nextTrip": "Neste generering",
		"tripsExist": "Turer kan allerede ha blitt generert fra denne malen. Venligst fjern koblingen manuelt dersom disse skal genereres på nytt"
	}
}
</i18n>

<template>
	<div class="frequency-selector-wrapper">
		<t-form-group rowGap="5px" style="grid-column: 1/5;">
			<t-checkbox
				v-model="autoSchedule"
				:label="$t('autoSchedule')"
			/>
			<div class="info-text">{{ $t('autoScheduleInfo') }}</div>
			<div
				v-if="originalType && frequency.type !== originalType && templateDetails.generatedTrips.length"
				class="info-text warning"
			>{{ $t('tripsExist') }}</div>
		</t-form-group>

		<t-form-group :title="$t('frequency')" style="grid-column: 1/2;">
			<t-select
				:items="frequencies"
				:selectedItem="frequency.type"
				:disabled="!autoSchedule"
				@select="setFrequencyType"
			>
				<template #displayedValue>
					{{ $t(frequency.type) || $t('selectFrequency') }}
				</template>

				<template #optionItemValue="{ item }">
					{{ $t(item) }}
				</template>
			</t-select>
		</t-form-group>
		<t-form-group :title="$t('startDate')" rowGap="5px" style="grid-column: 2/3;">
			<t-date-picker
				:value="displayDate(frequency.startDate)"
				:placeholder="$t('selectDate')"
				:minDate="today()"
				:dateFormat="displayDateFormat"
				:disabled="!autoSchedule"
				width="100%"
				@input="setFrequencyStartDate(storageDate($event))"
			/>
		</t-form-group>
		<t-form-group :title="$t('endDate')" rowGap="5px" style="grid-column: 3/4;">
			<t-date-picker
				:value="displayDate(frequency.endDate)"
				:placeholder="$t('selectDate')"
				:minDate="today()"
				:dateFormat="displayDateFormat"
				:disabled="!autoSchedule"
				width="100%"
				@input="setFrequencyEndDate(storageDate($event))"
			/>
		</t-form-group>

		<t-form-group
			v-if="templateDetails.nextTrip"
			:title="$t('nextTrip')"
			rowGap="5px"
			style="grid-column: 4/5;"
		>
			<t-text-input
				:value="displayDate(templateDetails.nextTrip)"
				disabled
			/>
		</t-form-group>

		<!-- Weekly selection -->
		<t-form-group v-if="autoSchedule && frequency.type === 'weekly'" rowGap="5px" style="grid-column: 1/5;">
			<div class="select-group">
				<t-checkbox
					v-for="day of days"
					:key="day"
					:checked="frequency.weekly[day]"
					:label="$t(day)"
					style="margin-right: 20px;"
					@change="setFrequencyWeekly({ day, value: $event })"
				/>
			</div>
		</t-form-group>

		<!-- Two weekly selection -->
		<t-form-group v-if="autoSchedule && frequency.type === 'twoWeekly'" rowGap="5px" style="grid-column: 1/5;">
			<div class="select-group">
				<span style="width: 80px; margin-right: 20px;">{{ $t('oddWeeks') }}:</span>
				<t-checkbox
					v-for="day of days"
					:key="day"
					:checked="frequency.twoWeekly.oddWeeks[day]"
					:label="$t(day)"
					style="margin-right: 20px;"
					@change="setFrequencyTwoWeekly({ week: 'oddWeeks', day, value: $event })"
				/>
			</div>
			<div class="select-group">
				<span style="width: 80px; margin-right: 20px;">{{ $t('evenWeeks') }}:</span>
				<t-checkbox
					v-for="day of days"
					:key="day"
					:checked="frequency.twoWeekly.evenWeeks[day]"
					:label="$t(day)"
					style="margin-right: 20px;"
					@change="setFrequencyTwoWeekly({ week: 'evenWeeks', day, value: $event })"
				/>
			</div>
		</t-form-group>

		<!-- Monthly scheduling selection -->
		<t-form-group v-if="autoSchedule && frequency.type === 'monthly'" rowGap="5px" style="grid-column: 1/5;">
			<div v-for="(value, name) in frequency.monthly" :key="name" class="select-group">
				<span>{{ $t('runEvery') }}</span>
				<t-select
					:items="numbers"
					:selectedItem="value.number"
					style="width: 65px;"
					@select="setFrequencyMonthly({ id: name, key: 'number', value: $event })"
				>
					<template #displayedValue>
						{{ value.number }}.
					</template>
					<template #optionItemValue="{ item }">
						{{ item }}.
					</template>
				</t-select>
				<t-select
					:items="days"
					:selectedItem="value.day"
					style="width: 200px;"
					@select="setFrequencyMonthly({ id: name, key: 'day', value: $event })"
				>
					<template #displayedValue>
						{{ value.day ? $t(value.day) : $t('selectDay') }}
					</template>
					<template #optionItemValue="{ item }">
						{{ $t(item) }}
					</template>
				</t-select>
				<t-icon-button
					v-if="frequency.monthly.length > 1"
					icon="minus"
					type="bp"
					size="small"
					iconSize="xs"
					appearance="minimal"
					@click="removeFrequencyDay(name)"
				/>
			</div>
			<t-icon-button
				icon="plus"
				type="bp"
				size="small"
				iconSize="xs"
				appearance="minimal"
				@click="addFrequencyDay()"
			/>
		</t-form-group>

		<!-- Specific dates selection -->
		<t-form-group v-if="autoSchedule && frequency.type === 'specificDates'" rowGap="5px" style="grid-column: 1/5">
			<span class="info-text" style="margin-bottom: 10px;">{{ $t('yearlyInfo') }}</span>
			<div v-for="(value, name) in frequency.specificDates" :key="name" class="select-group">
				<t-date-picker
					:value="value"
					:placeholder="$t('selectDate')"
					width="120px"
					dateFormat="DD.MM"
					headerFormat="MMMM"
					@input="setFrequencySpecificDates({ id: name, value: $event })"
				/>
				<t-icon-button
					v-if="frequency.specificDates.length > 1"
					icon="minus"
					type="bp"
					size="small"
					iconSize="xs"
					appearance="minimal"
					@click="removeFrequencyDay(name)"
				/>
			</div>
			<t-icon-button
				icon="plus"
				type="bp"
				size="small"
				iconSize="xs"
				appearance="minimal"
				@click="addFrequencyDay()"
			/>
		</t-form-group>
	</div>
</template>

<script>
import { mapState, mapActions } from '../store/tripTemplates.store';
import {
	storageDateFormat, displayDateFormat,
} from '@/core';
import moment from 'moment';

export default {
	name: 'FrequencySelector',

	data() {
		return {
			frequencies: [ 'daily', 'weekly', 'twoWeekly', 'monthly', 'specificDates' ],
			days: [ 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday' ],
			numbers: [ 1, 2, 3, 4 ],
			originalType: '',
		};
	},

	computed: {
		...mapState(['templateDetails']),
		frequency() {
			return this.templateDetails.frequency;
		},
		autoSchedule: {
			get() {
				return this.templateDetails.autoSchedule;
			},
			set(value) {
				this.setAutoSchedule(value);
			},
		},
		displayDateFormat() {
			return displayDateFormat;
		}
	},

	mounted() {
		this.originalType = this.templateDetails.frequency.type;
	},

	methods: {
		...mapActions([
			'setAutoSchedule',
			'setFrequencyType',
			'setFrequencyStartDate',
			'setFrequencyEndDate',
			'setFrequencyWeekly',
			'setFrequencyTwoWeekly',
			'setFrequencyMonthly',
			'setFrequencySpecificDates',
			'addFrequencyDay',
			'removeFrequencyDay',
		]),
		today() {
			return moment().startOf('day');
		},
		storageDate(displayDate) {
			if (displayDate) {
				return moment(displayDate, displayDateFormat).startOf('day').format(storageDateFormat);
			} else {
				return '';
			}
		},
		displayDate(storageDate) {
			if (storageDate) {
				return moment(storageDate, storageDateFormat).startOf('day').format(displayDateFormat);
			} else {
				return '';
			}
		},
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.frequency-selector-wrapper {
	grid-column: span 4;
	display: grid;
	grid-template-columns: repeat(4, minmax(min-content, 1fr));
	column-gap: 20px;
	row-gap: 20px;
}

.info-text {
	@extend %text-small;
	color: get-color($text, 'muted');
	padding-left: 10px;
	user-select: none;

	&.warning {
		color: get-color($text, 'danger')
	}
}

.select-group {
	display: flex;
	flex-direction: row;
	align-items: center;
	> * {
		margin-right: 10px;
	}
}
</style>
